import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import MainLayout from '../../components/layout/MainLayout';
import Avatar from '../../components/common/Avatar';
import { useTheme } from '../../contexts/ThemeContext';
import { 
  FileText,
  CheckCircle2,
  Clock,
  Eye,
  Loader2,
  Calendar,
  Euro,
  Globe,
  ChevronDown,
  ChevronRight,
  Star,
  Download,
  Wrench,
  Server,
  BarChart3,
  Timer,
  Search,
  ArrowLeft,
  ExternalLink,
  FileSpreadsheet
} from 'lucide-react';
import { toast } from 'sonner';
import api from '../../lib/axios';
import { cn } from '../../lib/utils';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';

const ClientForfaitsPage = () => {
  const navigate = useNavigate();
  const { theme } = useTheme();
  const [loading, setLoading] = useState(true);
  const [contracts, setContracts] = useState([]);
  const [selectedContract, setSelectedContract] = useState(null);
  const [tasks, setTasks] = useState([]);
  const [reports, setReports] = useState([]);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [expandedReports, setExpandedReports] = useState({});
  const [searchQuery, setSearchQuery] = useState('');
  
  // Report detail modal
  const [selectedReport, setSelectedReport] = useState(null);
  const [showReportModal, setShowReportModal] = useState(false);

  useEffect(() => {
    fetchContracts();
  }, []);

  const fetchContracts = async () => {
    try {
      setLoading(true);
      const res = await api.get('/contracts/my-forfaits');
      setContracts(res.data || []);
    } catch (error) {
      console.error('Error fetching forfaits:', error);
      toast.error('Erreur lors du chargement des forfaits');
    } finally {
      setLoading(false);
    }
  };

  const selectContract = async (contract) => {
    setSelectedContract(contract);
    setLoadingDetail(true);
    setTasks([]);
    setReports([]);
    
    try {
      // Fetch tasks and reports for this contract
      const [tasksRes, reportsRes] = await Promise.all([
        api.get(`/contracts/${contract.id}/tasks`).catch(() => ({ data: [] })),
        api.get(`/contracts/${contract.id}/client-reports`).catch(() => ({ data: [] }))
      ]);
      
      setTasks(tasksRes.data || []);
      setReports(reportsRes.data || []);
    } catch (error) {
      console.error('Error fetching contract details:', error);
    } finally {
      setLoadingDetail(false);
    }
  };

  const toggleReport = (reportId) => {
    setExpandedReports(prev => ({
      ...prev,
      [reportId]: !prev[reportId]
    }));
  };

  const formatDuration = (minutes) => {
    if (!minutes) return '-';
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    if (hours === 0) return `${mins}min`;
    if (mins === 0) return `${hours}h`;
    return `${hours}h${mins}min`;
  };

  const getStatusBadge = (status) => {
    const config = {
      'active': { label: 'Actif', className: 'bg-emerald-100 text-emerald-700' },
      'pending': { label: 'En attente', className: 'bg-amber-100 text-amber-700' },
      'cancelled': { label: 'Annulé', className: 'bg-red-100 text-red-700' },
      'expired': { label: 'Expiré', className: 'bg-slate-100 text-slate-700' }
    };
    const c = config[status] || config['active'];
    return <Badge className={c.className}>{c.label}</Badge>;
  };

  const getTaskStatusBadge = (status) => {
    const config = {
      'completed': { label: 'Terminé', className: 'bg-green-100 text-green-700' },
      'in_progress': { label: 'En cours', className: 'bg-blue-100 text-blue-700' },
      'scheduled': { label: 'Planifié', className: 'bg-purple-100 text-purple-700' },
      'pending': { label: 'En attente', className: 'bg-slate-100 text-slate-700' }
    };
    const c = config[status] || config['pending'];
    return <Badge className={cn("text-[10px] h-5", c.className)}>{c.label}</Badge>;
  };

  const getContractTypeIcon = (type) => {
    if (type === 'hosting') return <Server className="w-5 h-5 text-blue-500" />;
    if (type === 'maintenance') return <Wrench className="w-5 h-5 text-green-500" />;
    return <FileText className="w-5 h-5 text-purple-500" />;
  };

  const getContractTypeLabel = (type) => {
    const labels = {
      'hosting': 'Hébergement',
      'maintenance': 'Maintenance',
      'both': 'Hébergement & Maintenance',
      'services': 'Services mensuels'
    };
    return labels[type] || type;
  };

  // Generate PDF report
  const generateReportPDF = (report) => {
    const doc = new jsPDF();
    
    // Header
    doc.setFontSize(18);
    doc.text(`Rapport d'activité - ${report.month_label || report.month}`, 14, 20);
    
    doc.setFontSize(11);
    doc.setTextColor(100);
    doc.text(`Contrat: ${selectedContract?.name || 'N/A'}`, 14, 30);
    doc.text(`Total: ${report.total_hours?.toFixed(1) || 0}h`, 14, 37);
    
    // Tasks table
    const taskRows = (report.tasks || []).map(task => [
      task.task_name || task.title || 'Tâche',
      task.assignee_name || task.assignee?.first_name || '-',
      formatDuration(task.consumed_minutes || task.duration_minutes),
      task.completed_at ? new Date(task.completed_at).toLocaleDateString('fr-FR') : '-'
    ]);
    
    autoTable(doc, {
      startY: 45,
      head: [['Tâche', 'Intervenant', 'Durée', 'Date']],
      body: taskRows,
      theme: 'striped',
      headStyles: { fillColor: [59, 130, 246] }
    });
    
    doc.save(`rapport-${report.month || 'mensuel'}.pdf`);
    toast.success('PDF téléchargé');
  };

  // Export CSV
  const exportReportCSV = (report) => {
    const headers = ['Tâche', 'Intervenant', 'Durée (min)', 'Date'];
    const rows = (report.tasks || []).map(task => [
      task.task_name || task.title || 'Tâche',
      task.assignee_name || task.assignee?.first_name || '-',
      task.consumed_minutes || task.duration_minutes || 0,
      task.completed_at ? new Date(task.completed_at).toLocaleDateString('fr-FR') : '-'
    ]);
    
    const csvContent = [headers, ...rows].map(row => row.join(',')).join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `rapport-${report.month || 'mensuel'}.csv`;
    a.click();
    toast.success('CSV téléchargé');
  };

  // Get unique assignees from report
  const getReportAssignees = (report) => {
    const assignees = [];
    const seen = new Set();
    (report.tasks || []).forEach(task => {
      const assignee = task.assignee || (task.assignee_name ? { first_name: task.assignee_name, last_name: '' } : null);
      if (assignee && !seen.has(task.assignee_id || task.assignee_name)) {
        seen.add(task.assignee_id || task.assignee_name);
        assignees.push(assignee);
      }
    });
    return assignees;
  };

  const filteredContracts = contracts.filter(c => 
    c.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    c.website_url?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  if (loading) {
    return (
      <MainLayout>
        <div className="flex items-center justify-center h-[60vh]">
          <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
        </div>
      </MainLayout>
    );
  }

  // Detail view when a contract is selected
  if (selectedContract) {
    return (
      <MainLayout>
        <div className="space-y-6">
          {/* Back button & Header */}
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <div className="flex items-center gap-4">
              <Button 
                variant="ghost" 
                size="icon"
                onClick={() => setSelectedContract(null)}
                className="rounded-full"
              >
                <ArrowLeft className="w-5 h-5" />
              </Button>
              <div>
                <div className="flex items-center gap-3">
                  {getContractTypeIcon(selectedContract.contract_type)}
                  <h1 className="text-xl sm:text-2xl font-bold" style={{ color: theme.colors?.primary }}>
                    {selectedContract.name}
                  </h1>
                  {getStatusBadge(selectedContract.status)}
                </div>
                {selectedContract.website_url && (
                  <a 
                    href={selectedContract.website_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-1 text-sm text-blue-600 hover:underline mt-1"
                  >
                    <Globe className="w-4 h-4" />
                    {selectedContract.website_url.replace(/^https?:\/\//, '')}
                    <ExternalLink className="w-3 h-3" />
                  </a>
                )}
              </div>
            </div>
          </div>

          {/* Stats Cards */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4">
            <Card>
              <CardContent className="p-3 sm:p-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ backgroundColor: `${theme.colors?.accent}20` }}>
                    <Euro className="w-5 h-5" style={{ color: theme.colors?.accent }} />
                  </div>
                  <div>
                    <p className="text-xs text-slate-500">Prix mensuel</p>
                    <p className="text-lg sm:text-xl font-bold" style={{ color: theme.colors?.primary }}>
                      {(selectedContract.monthly_price || 0).toLocaleString('fr-FR')}€
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="p-3 sm:p-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl flex items-center justify-center bg-blue-100">
                    <Clock className="w-5 h-5 text-blue-600" />
                  </div>
                  <div>
                    <p className="text-xs text-slate-500">Heures utilisées</p>
                    <p className="text-lg sm:text-xl font-bold" style={{ color: theme.colors?.primary }}>
                      {(selectedContract.hours_used || 0).toFixed(1)}h / {selectedContract.hours_included || 0}h
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="p-3 sm:p-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl flex items-center justify-center bg-green-100">
                    <CheckCircle2 className="w-5 h-5 text-green-600" />
                  </div>
                  <div>
                    <p className="text-xs text-slate-500">Tâches terminées</p>
                    <p className="text-lg sm:text-xl font-bold" style={{ color: theme.colors?.primary }}>
                      {tasks.filter(t => t.status === 'completed').length}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="p-3 sm:p-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl flex items-center justify-center bg-purple-100">
                    <Calendar className="w-5 h-5 text-purple-600" />
                  </div>
                  <div>
                    <p className="text-xs text-slate-500">Engagement</p>
                    <p className="text-lg sm:text-xl font-bold" style={{ color: theme.colors?.primary }}>
                      {selectedContract.engagement || '-'}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {loadingDetail ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
            </div>
          ) : (
            <Tabs defaultValue="tasks" className="space-y-4">
              <TabsList className="bg-white border flex-wrap h-auto p-1 gap-1">
                <TabsTrigger value="tasks" className="gap-1.5 px-3 py-2 text-xs sm:text-sm data-[state=active]:bg-slate-100">
                  <Wrench className="w-4 h-4" />
                  <span>Tâches</span>
                </TabsTrigger>
                <TabsTrigger value="reports" className="gap-1.5 px-3 py-2 text-xs sm:text-sm data-[state=active]:bg-slate-100">
                  <BarChart3 className="w-4 h-4" />
                  <span>Rapports mensuels</span>
                </TabsTrigger>
              </TabsList>

              {/* Tasks Tab */}
              <TabsContent value="tasks">
                <Card>
                  <CardHeader className="py-3">
                    <CardTitle className="text-base">Interventions ({tasks.length})</CardTitle>
                    <CardDescription className="text-sm">Tâches réalisées et planifiées sur ce contrat</CardDescription>
                  </CardHeader>
                  <CardContent className="p-0">
                    {tasks.length === 0 ? (
                      <div className="text-center py-12">
                        <Wrench className="w-12 h-12 mx-auto mb-4 text-slate-300" />
                        <p className="text-slate-500">Aucune tâche pour ce contrat</p>
                      </div>
                    ) : (
                      <div className="overflow-x-auto">
                        <Table>
                          <TableHeader>
                            <TableRow className="bg-slate-50/50 hover:bg-slate-50/50">
                              <TableHead className="text-xs font-medium w-[40%]">Tâche</TableHead>
                              <TableHead className="text-xs font-medium hidden sm:table-cell">Intervenant</TableHead>
                              <TableHead className="text-xs font-medium text-center hidden md:table-cell">Durée</TableHead>
                              <TableHead className="text-xs font-medium text-center hidden sm:table-cell">Date</TableHead>
                              <TableHead className="text-xs font-medium">Statut</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {tasks.map((task) => (
                              <TableRow 
                                key={task.id} 
                                className={cn(
                                  task.status === 'completed' && "bg-green-50/30",
                                  task.status === 'in_progress' && "bg-blue-50/30"
                                )}
                              >
                                <TableCell className="py-2">
                                  <div className="flex items-center gap-2">
                                    {task.task_type === 'client_request' && (
                                      <Badge className="bg-orange-100 text-orange-700 text-[10px] h-5">Ma demande</Badge>
                                    )}
                                    <span className="font-medium text-sm">{task.title}</span>
                                  </div>
                                  {task.description && (
                                    <p className="text-xs text-slate-500 mt-0.5 line-clamp-1">{task.description}</p>
                                  )}
                                </TableCell>
                                <TableCell className="py-2 hidden sm:table-cell">
                                  {task.assignee_name || task.assignee ? (
                                    <div className="flex items-center gap-2">
                                      <Avatar 
                                        name={task.assignee_name || `${task.assignee?.first_name} ${task.assignee?.last_name}`} 
                                        size="xs" 
                                      />
                                      <span className="text-sm text-slate-600">
                                        {task.assignee_name || `${task.assignee?.first_name} ${task.assignee?.last_name}`}
                                      </span>
                                    </div>
                                  ) : (
                                    <span className="text-sm text-slate-400 italic">-</span>
                                  )}
                                </TableCell>
                                <TableCell className="py-2 text-center hidden md:table-cell">
                                  <span className="text-xs font-mono text-slate-600">
                                    {formatDuration(task.duration_minutes)}
                                  </span>
                                </TableCell>
                                <TableCell className="py-2 text-center hidden sm:table-cell">
                                  <span className="text-xs text-slate-500">
                                    {task.completed_at 
                                      ? new Date(task.completed_at).toLocaleDateString('fr-FR')
                                      : task.scheduled_date 
                                        ? task.scheduled_date.split('T')[0]
                                        : '-'
                                    }
                                  </span>
                                </TableCell>
                                <TableCell className="py-2">
                                  {getTaskStatusBadge(task.status)}
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </div>
                    )}
                  </CardContent>
                </Card>
              </TabsContent>

              {/* Reports Tab */}
              <TabsContent value="reports">
                <Card>
                  <CardHeader>
                    <CardTitle>Rapports mensuels</CardTitle>
                    <CardDescription>Cliquez sur un rapport pour voir le détail des interventions</CardDescription>
                  </CardHeader>
                  <CardContent>
                    {reports.length === 0 ? (
                      <div className="text-center py-12">
                        <FileText className="w-12 h-12 mx-auto mb-4 text-slate-300" />
                        <p className="text-slate-500">Aucun rapport disponible</p>
                        <p className="text-sm text-slate-400 mt-2">Les rapports seront générés automatiquement à la fin de chaque mois</p>
                      </div>
                    ) : (
                      <div className="space-y-3">
                        {reports.map((report) => {
                          const isExpanded = expandedReports[report.id];
                          const assignees = getReportAssignees(report);
                          
                          return (
                            <div key={report.id} className="rounded-xl border overflow-hidden">
                              {/* Report Header - Clickable */}
                              <button
                                onClick={() => toggleReport(report.id)}
                                className="w-full flex flex-col sm:flex-row sm:items-center justify-between p-3 sm:p-4 hover:bg-slate-50 transition-colors text-left gap-3"
                              >
                                <div className="flex items-center gap-3 sm:gap-4">
                                  <div className="w-10 h-10 rounded-xl flex items-center justify-center bg-slate-100 flex-shrink-0">
                                    <FileText className="w-5 h-5 text-slate-600" />
                                  </div>
                                  <div>
                                    <h4 className="font-medium text-sm sm:text-base" style={{ color: theme.colors?.primary }}>
                                      {report.month_label || report.month}
                                    </h4>
                                    <p className="text-xs sm:text-sm text-slate-500">
                                      {(report.tasks || []).length} tâches • {(report.total_hours || 0).toFixed(1)}h
                                    </p>
                                  </div>
                                </div>
                                
                                <div className="flex items-center gap-2 sm:gap-4 justify-between sm:justify-end w-full sm:w-auto">
                                  {/* Avatar Stack */}
                                  <div className="hidden md:flex -space-x-2">
                                    {assignees.slice(0, 3).map((assignee, idx) => (
                                      <div key={idx} className="relative" style={{ zIndex: assignees.length - idx }}>
                                        <Avatar
                                          name={`${assignee.first_name} ${assignee.last_name}`}
                                          size="sm"
                                          className="ring-2 ring-white"
                                        />
                                      </div>
                                    ))}
                                    {assignees.length > 3 && (
                                      <div className="w-8 h-8 rounded-full bg-slate-200 flex items-center justify-center text-xs font-medium text-slate-600 ring-2 ring-white">
                                        +{assignees.length - 3}
                                      </div>
                                    )}
                                  </div>
                                  
                                  <Badge className="bg-green-100 text-green-700 text-xs">
                                    {report.status === 'validated' ? 'Validé' : report.status === 'draft' ? 'Brouillon' : 'En cours'}
                                  </Badge>
                                  
                                  <div className="flex items-center gap-1">
                                    <Button 
                                      variant="outline" 
                                      size="sm" 
                                      className="rounded-full h-8 px-2 sm:px-3" 
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        generateReportPDF(report);
                                      }}
                                    >
                                      <Download className="w-4 h-4" />
                                      <span className="hidden sm:inline ml-1">PDF</span>
                                    </Button>
                                    
                                    <Button 
                                      variant="ghost" 
                                      size="sm" 
                                      className="rounded-full h-8 w-8 p-0" 
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        exportReportCSV(report);
                                      }}
                                    >
                                      <FileSpreadsheet className="w-4 h-4" />
                                    </Button>
                                  </div>
                                  
                                  {isExpanded ? (
                                    <ChevronDown className="w-5 h-5 text-slate-400" />
                                  ) : (
                                    <ChevronRight className="w-5 h-5 text-slate-400" />
                                  )}
                                </div>
                              </button>
                              
                              {/* Expanded Content - Task Details */}
                              {isExpanded && (
                                <div className="border-t bg-slate-50">
                                  <div className="p-3 sm:p-4 overflow-x-auto">
                                    <table className="w-full min-w-[400px]">
                                      <thead>
                                        <tr className="text-xs text-slate-500 uppercase tracking-wider">
                                          <th className="text-left pb-3 font-medium">Tâche</th>
                                          <th className="text-left pb-3 font-medium hidden sm:table-cell">Intervenant</th>
                                          <th className="text-left pb-3 font-medium">Durée</th>
                                          <th className="text-left pb-3 font-medium hidden md:table-cell">Date</th>
                                        </tr>
                                      </thead>
                                      <tbody className="divide-y divide-slate-200">
                                        {(report.tasks || []).map((task, idx) => (
                                          <tr key={idx} className="bg-white">
                                            <td className="py-3 pr-4">
                                              <p className="font-medium text-sm" style={{ color: theme.colors?.primary }}>
                                                {task.task_name || task.title}
                                              </p>
                                              {task.project_name && (
                                                <p className="text-xs text-slate-500 mt-0.5">{task.project_name}</p>
                                              )}
                                            </td>
                                            <td className="py-3 pr-4 hidden sm:table-cell">
                                              {(task.assignee || task.assignee_name) ? (
                                                <div className="flex items-center gap-2">
                                                  <Avatar
                                                    name={task.assignee_name || `${task.assignee?.first_name} ${task.assignee?.last_name}`}
                                                    size="sm"
                                                  />
                                                  <span className="text-sm" style={{ color: theme.colors?.primary }}>
                                                    {task.assignee_name || task.assignee?.first_name}
                                                  </span>
                                                </div>
                                              ) : (
                                                <span className="text-sm text-slate-400">-</span>
                                              )}
                                            </td>
                                            <td className="py-3 pr-4">
                                              <div className="flex items-center gap-1.5">
                                                <Timer className="w-4 h-4 text-slate-400" />
                                                <span className="font-mono text-sm" style={{ color: theme.colors?.primary }}>
                                                  {formatDuration(task.consumed_minutes || task.duration_minutes)}
                                                </span>
                                              </div>
                                            </td>
                                            <td className="py-3 hidden md:table-cell">
                                              <span className="text-xs text-slate-500">
                                                {task.completed_at 
                                                  ? new Date(task.completed_at).toLocaleDateString('fr-FR')
                                                  : '-'
                                                }
                                              </span>
                                            </td>
                                          </tr>
                                        ))}
                                      </tbody>
                                    </table>
                                    
                                    {/* Total row */}
                                    <div className="mt-4 pt-3 border-t flex justify-between items-center">
                                      <span className="text-sm font-medium text-slate-600">Total du mois</span>
                                      <span className="text-lg font-bold" style={{ color: theme.colors?.accent }}>
                                        {(report.total_hours || 0).toFixed(1)}h
                                      </span>
                                    </div>
                                  </div>
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </CardContent>
                </Card>
              </TabsContent>
            </Tabs>
          )}
        </div>
      </MainLayout>
    );
  }

  // List view - showing all contracts
  return (
    <MainLayout>
      <div className="space-y-6">
        {/* Header */}
        <div>
          <h1 className="text-2xl font-bold" style={{ color: theme.colors?.primary }}>
            Mes forfaits
          </h1>
          <p className="text-slate-500">Suivi de vos contrats d'hébergement et maintenance</p>
        </div>

        {/* Stats Summary */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-emerald-100">
                  <FileText className="w-5 h-5 text-emerald-600" />
                </div>
                <div>
                  <p className="text-xs text-slate-500">Forfaits actifs</p>
                  <p className="text-xl font-bold">{contracts.filter(c => c.status === 'active').length}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-blue-100">
                  <Server className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <p className="text-xs text-slate-500">Hébergement</p>
                  <p className="text-xl font-bold">
                    {contracts.filter(c => c.contract_type === 'hosting' || c.contract_type === 'both').length}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-green-100">
                  <Wrench className="w-5 h-5 text-green-600" />
                </div>
                <div>
                  <p className="text-xs text-slate-500">Maintenance</p>
                  <p className="text-xl font-bold">
                    {contracts.filter(c => c.contract_type === 'maintenance' || c.contract_type === 'both').length}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg" style={{ backgroundColor: `${theme.colors?.accent}20` }}>
                  <Euro className="w-5 h-5" style={{ color: theme.colors?.accent }} />
                </div>
                <div>
                  <p className="text-xs text-slate-500">Budget mensuel</p>
                  <p className="text-xl font-bold">
                    {contracts.reduce((sum, c) => sum + (c.monthly_price || 0), 0)}€
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Search */}
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input
            placeholder="Rechercher un forfait ou un site..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10"
          />
        </div>

        {/* Contracts List */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <BarChart3 className="w-5 h-5" style={{ color: theme.colors?.accent }} />
              Mes contrats ({filteredContracts.length})
            </CardTitle>
            <CardDescription>
              Cliquez sur un contrat pour voir les détails et rapports
            </CardDescription>
          </CardHeader>
          <CardContent>
            {filteredContracts.length === 0 ? (
              <div className="text-center py-12 text-slate-500">
                <FileText className="w-12 h-12 mx-auto mb-3 opacity-30" />
                <p>Aucun forfait trouvé</p>
              </div>
            ) : (
              <div className="space-y-3">
                {filteredContracts.map(contract => (
                  <div 
                    key={contract.id} 
                    className="p-4 border rounded-xl hover:shadow-md transition-shadow cursor-pointer"
                    onClick={() => selectContract(contract)}
                    data-testid={`forfait-row-${contract.id}`}
                  >
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                      <div className="flex items-start gap-3">
                        <div className="w-10 h-10 rounded-xl flex items-center justify-center bg-slate-100 flex-shrink-0">
                          {getContractTypeIcon(contract.contract_type)}
                        </div>
                        <div className="min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <h3 className="font-semibold truncate">{contract.name}</h3>
                            {getStatusBadge(contract.status)}
                          </div>
                          <div className="flex flex-wrap gap-x-4 gap-y-1 mt-1 text-sm text-slate-500">
                            <span className="flex items-center gap-1">
                              {getContractTypeIcon(contract.contract_type)}
                              <span className="text-xs">{getContractTypeLabel(contract.contract_type)}</span>
                            </span>
                            {contract.website_url && (
                              <span className="flex items-center gap-1 text-blue-600">
                                <Globe className="w-3 h-3" />
                                <span className="text-xs truncate max-w-[150px]">
                                  {contract.website_url.replace(/^https?:\/\//, '')}
                                </span>
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-4 sm:gap-6">
                        <div className="text-center">
                          <p className="text-xs text-slate-500">Heures</p>
                          <p className="font-bold text-sm">
                            {(contract.hours_used || 0).toFixed(1)}h / {contract.hours_included || 0}h
                          </p>
                        </div>
                        <div className="text-right">
                          <p className="text-xs text-slate-500">Mensuel</p>
                          <p className="font-bold text-lg" style={{ color: theme.colors?.accent }}>
                            {(contract.monthly_price || 0)}€
                          </p>
                        </div>
                        <ChevronRight className="w-5 h-5 text-slate-400 hidden sm:block" />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </MainLayout>
  );
};

export default ClientForfaitsPage;
